import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import useGetActiveCart from '../../hooks/queries/useGetActiveCart/useGetActiveCart';
import { StripeElementsContextProps, StripeOptions } from './types';
import {
  CHECKOUT_URLS,
    AFFIRM_PRICE_LOWER_THRESHOLD,
    AFFIRM_PRICE_UPPER_THRESHOLD,
} from '../../utils/common/constants';
import { CartInStore } from '../../slices/cart/types';


const StripeElementsContext: React.FC<StripeElementsContextProps> = ({
  children,
}) => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [stripeIsLoaded, setStripeIsLoaded] = useState<boolean>(false);

  const {
    isDraft,
    status: cartStatus,
    lineItems,
    taxedPrice,
  } = useSelector((state: { cart: CartInStore }) => state.cart);

  useGetActiveCart({ isDraft });

  const cartTotalCentAmount =
    taxedPrice && taxedPrice.totalGross
      ? taxedPrice.totalGross.centAmount
      : 0;

  const enableChargeAfter = window?.purTogglesEnabled(
    'charge_after_regular_checkout',
  );

  const isCheckout = CHECKOUT_URLS.includes(window.location.pathname);

  const enableAcima = window?.purTogglesEnabled('acima_payment');

  const enableShopPay = window?.purTogglesEnabled('shop_pay_regular_checkout');

  const enableTruemed = window?.purTogglesEnabled('truemed_payment');

  // Use Stripe's Affirm, not ours.
  const enableStripeAffirm = window?.purTogglesEnabled('stripe_affirm');

  // Stripe custom payment method configuration ID.
  const enableCustomPmcId = window?.purTogglesEnabled('stripe_custom_pmc');

  const amount = useSelector(
    (state: {
      cart: {
        taxedPrice: {
          totalGross: { centAmount: number; fractionDigits: number };
        };
        estimatedTaxAmount: { centAmount: number };
        totalPrice: { centAmount: number; fractionDigits: number };
      };
    }) => {
      if (state.cart?.taxedPrice) {
        return state.cart?.taxedPrice.totalGross;
      }

      const estimatedTaxAmount: number =
        state.cart?.estimatedTaxAmount?.centAmount ?? 0;
      const totalPriceCentAmount: number =
        state.cart?.totalPrice.centAmount ?? 0;

      return {
        ...state.cart?.totalPrice,
        centAmount: totalPriceCentAmount + estimatedTaxAmount,
      };
    },
  );

  const isAmountGreaterThanZero = amount?.centAmount > 0;

  // Get some CSS values so we can pass them to Stripe.
  const root = document.querySelector(':root') as Element;
  const styles = getComputedStyle(root);

  const options: StripeOptions = {
    amount: amount.centAmount,
    // For $0 orders, we set the "setup" mode - used for saving payment
    // information for future capture, but we use it here just to avoid errors.
    mode: isAmountGreaterThanZero ? 'payment' : 'setup',
    captureMethod: 'manual',
    currency: 'usd',
    fonts: [
      {
        family: 'Graphik',
        src: 'url(https://purple.com/themes/custom/themekit/assets/fonts/Graphik/GraphikRegular.woff2)',
        weight: '500',
      },
    ],
    appearance: {
      variables: {
        colorPrimary: styles.getPropertyValue('--color-brand-primary').trim(),
        fontFamily: styles.getPropertyValue('--ff-secondary').trim(),
        borderRadius: '0px',
        buttonBorderRadius: styles
          .getPropertyValue('--btn-border-radius')
          .trim(),
      },
      rules: {
        '.AccordionItem': {
          borderRadius: '0.5rem',
        },
      },
    },
    customPaymentMethods: [],
  };

  if (isAmountGreaterThanZero && enableCustomPmcId) {
    // We pass the custom payment method configuration ID to load in a different
    // set of payment methods.
    options.payment_method_configuration = process.env.STRIPE_AB_PAYMENT_METHOD_CONFIGURATION_ID;
  }

  if (enableShopPay) {
    options.customPaymentMethods?.push({
      id: process.env.STRIPE_CUSTOM_SHOP_PAY_ID ?? '',
      options: {
        type: 'static',
        subtitle: 'Pay with Shop Pay',
      },
    });
  }

  if (enableAcima && !isDraft) {
    options.customPaymentMethods?.push({
      id: process.env.STRIPE_CUSTOM_ACIMA_ID ?? '',
      options: {
        type: 'static',
        subtitle: 'The no credit option',
      },
    });
  }

  if (enableTruemed) {
    options.customPaymentMethods?.push({
      id: process.env.STRIPE_CUSTOM_TRUEMED_ID ?? '',
      options: {
        type: 'static',
        subtitle: 'Pay with HSA/FSA',
      },
    });
  }

  if (enableChargeAfter || isDraft) {
    options.customPaymentMethods?.push({
      id: process.env.STRIPE_CUSTOM_CHARGEAFTER_ID ?? '',
      options: {
        type: 'static',
        subtitle: 'Pay with Purple Credit Card',
      },
    });
  }

  if (!enableStripeAffirm &&
    cartTotalCentAmount >= AFFIRM_PRICE_LOWER_THRESHOLD && 
    cartTotalCentAmount <= AFFIRM_PRICE_UPPER_THRESHOLD) {
    options.customPaymentMethods?.push({
      id: process.env.STRIPE_CUSTOM_AFFIRM_ID ?? '',
      options: {
        type: 'static',
        subtitle: '0% APR for 60 months on orders $2000+',
      },
    });
  }

  useEffect(() => {
    const callLoadStripe = () => {
      const stripeClientApiKey = process.env.STRIPE_CLIENT_API_KEY || '';
      const stripePromiseToAssign = loadStripe(stripeClientApiKey);
      setStripePromise(stripePromiseToAssign);
      setStripeIsLoaded(true);
    };
    if (!stripeIsLoaded) {
      callLoadStripe();
    }
  }, [stripeIsLoaded, stripePromise]);

  useEffect(() => {
    if (isCheckout && cartStatus === 'succeeded' && !lineItems?.length) {
      // cart is empty, then redirect to cart page
      window.location.href = '/cart';
    }
  }, [cartStatus, isCheckout, lineItems?.length]);

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};

export default StripeElementsContext;
